import clsx from 'clsx';
import AnimatedCrossIcon from './icons/AnimatedCrossIcon';

/**
 * A toast with an animated close button set with a timer.
 *
 * content: Any React component to display in the toast.
 * closed: A flag that, when true, triggers the closing animation.
 * id: A unique ID for the toast.
 * type: "neutral" | "error" | "positive" | "warning".
 * timer: The time in ms before the toast is removed.
 * removeToast: A function to handle removing the toast.
 */
export default function Toast({
  content,
  closed,
  id,
  type,
  timer,
  removeToast
}) {
  const typeBasedStyles = getStylesForType(type);

  return (
    <div
      data-cy="toast"
      className={clsx(
        'pointer-events-auto relative mb-2 w-full max-w-[335px] overflow-hidden rounded border shadow-[2px_4px_12px_0px_rgba(0,0,0,0.4)] ring-1 ring-black/5 transition-all ease-linear [--translate-y-amount:2rem] sm:[--translate-x-amount:2rem] sm:[--translate-y-amount:0rem]',
        typeBasedStyles,
        {
          'animate-add-toast': !closed,
          'animate-remove-toast': closed
        }
      )}
    >
      {content}
      <button
        className="absolute top-2 right-2 inline-flex focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-none"
        onClick={() => removeToast(id)}
      >
        <span className="sr-only">Close</span>

        <AnimatedCrossIcon duration={timer} />
      </button>
    </div>
  );
}

/**
 * Each toast type has a different background color and border color.
 */
function getStylesForType(type) {
  switch (type) {
    case 'error':
      return 'bg-cozy-cognac-10 border-cozy-cognac';
    case 'warning':
      return 'bg-warm-pewter-60 border-gold';
    case 'positive':
      return 'bg-parlor-green-5 border-parlor-green-60';
    default:
      return 'bg-white border-parlor-green-60';
  }
}
